import { useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Loader2, Camera, ImagePlus, Plus, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useImageGeneration } from "../../context/ImageGenerationContext";

export function BackgroundPhotosCard({ restaurantId }) {
  const { restaurantBgPhotos, setRestaurantBgPhotos } = useImageGeneration();
  const [uploadingBg, setUploadingBg] = useState(false);
  const bgPhotoInputRef = useRef(null);

  const handleUploadBgPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const oversized = files.find(f => f.size > 5 * 1024 * 1024);
    if (oversized) { toast.error("Alguna imagen supera los 5MB."); return; }
    setUploadingBg(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const result = await api.uploadImage(file);
        if (result.success) newUrls.push(result.image_url);
      }
      if (newUrls.length > 0) {
        const updated = [...restaurantBgPhotos, ...newUrls];
        setRestaurantBgPhotos(updated);
        await api.updateBackgroundPhotos(restaurantId, updated);
        toast.success(`${newUrls.length} foto${newUrls.length > 1 ? "s" : ""} guardada${newUrls.length > 1 ? "s" : ""}`);
      }
    } catch {
      toast.error("Error al subir las fotos");
    } finally {
      setUploadingBg(false);
      if (bgPhotoInputRef.current) bgPhotoInputRef.current.value = "";
    }
  };

  const handleRemoveBgPhoto = async (idx) => {
    const updated = restaurantBgPhotos.filter((_, i) => i !== idx);
    setRestaurantBgPhotos(updated);
    try {
      await api.updateBackgroundPhotos(restaurantId, updated);
      toast.success("Foto eliminada");
    } catch {
      toast.error("Error al eliminar la foto");
    }
  };

  return (
    <Card className="border-none shadow-soft">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary" />
          Fotos del restaurante
        </CardTitle>
        <CardDescription>
          Imágenes de tu local que la IA usará como fondo al generar las fotos de los platos.
          Sube varias para que cada plato tenga un ambiente diferente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {restaurantBgPhotos.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {restaurantBgPhotos.map((photo, idx) => (
              <div key={idx} className="relative group shrink-0">
                <img
                  src={photo}
                  alt={`Foto ${idx + 1}`}
                  className="w-24 h-16 object-cover rounded-lg border border-border/60 shadow-sm"
                />
                <button
                  onClick={() => handleRemoveBgPhoto(idx)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-destructive-foreground rounded-full hidden group-hover:flex items-center justify-center text-xs shadow-md"
                  data-testid={`settings-remove-bg-${idx}`}
                >×</button>
                <div className="absolute bottom-1 right-1 bg-black/50 text-white text-[10px] px-1 rounded">
                  {idx + 1}
                </div>
              </div>
            ))}
            <button
              onClick={() => bgPhotoInputRef.current?.click()}
              disabled={uploadingBg}
              className="w-24 h-16 border-2 border-dashed border-border/50 rounded-lg flex flex-col items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors shrink-0"
              data-testid="settings-add-bg-photo-btn"
            >
              {uploadingBg ? <Loader2 className="w-5 h-5 animate-spin" /> : <Plus className="w-5 h-5" />}
            </button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-border/50 rounded-xl p-8 text-center">
            <ImagePlus className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm font-medium mb-1">Sin fotos aún</p>
            <p className="text-xs text-muted-foreground mb-4">
              Sube fotos de tu barra, terraza o salón para personalizar las imágenes generadas por IA
            </p>
            <Button
              variant="outline"
              size="sm"
              className="rounded-full gap-2"
              onClick={() => bgPhotoInputRef.current?.click()}
              disabled={uploadingBg}
              data-testid="settings-upload-bg-photos-btn"
            >
              {uploadingBg
                ? <><Loader2 className="w-4 h-4 animate-spin" />Subiendo...</>
                : <><Plus className="w-4 h-4" />Añadir fotos del restaurante</>
              }
            </Button>
          </div>
        )}
        <input
          type="file"
          ref={bgPhotoInputRef}
          onChange={handleUploadBgPhotos}
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
        />
        {restaurantBgPhotos.length > 0 && (
          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            {restaurantBgPhotos.length} foto{restaurantBgPhotos.length > 1 ? "s" : ""} guardada{restaurantBgPhotos.length > 1 ? "s" : ""} ·
            La IA las combinará aleatoriamente al generar imágenes de platos
          </p>
        )}
      </CardContent>
    </Card>
  );
}
